import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { IoIosSearch } from "react-icons/io";
import {
  Upload,
  FileSpreadsheet,
  X,
  Check,
  CheckCircle2,
  AlertCircle,
  Archive,
  History,
  Mail,
  Copy,
  FileText,
  Send,
  Link2,
} from "lucide-react";
import { useEtatGlobal } from "../EtatGlobal.js";
import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";
import { useClients } from "../Portefeuille/useClients.js";
import BadgeBloque from "../Suivis/Annexe/BadgeBloque";
import "../Suivis/Suivi.css"; // classe .ligne-bloquee (dossier bloqué)
import {
  chargerRecouvrement,
  chargerJournal,
  importerRecouvrement,
  modifierDossier,
  marquerEnvoyee as apiMarquerEnvoyee,
  cloturerDossier,
  enregistrerModeles,
  envoyerRelance,
} from "./recouvrementApi";
import { indexerClients, proposerClient } from "./recouvrementClients";
import { genererCourrierPdf, courrierPdfBase64, nomFichierCourrier } from "./recouvrementPdf";

// Recouvrement — écran de suivi des créances clients et de leur relance.
//
// Les dossiers, le journal, l'historique des imports et les modèles de mail
// sont enregistrés dans la base (API /api/recouvrement) et partagés entre
// collaborateurs. Un dossier rattaché à un client MyALFA alimente l'historique
// de sa fiche à chaque relance, passage de niveau et clôture.

const PAGE_SIZE = 20;

// Colonnes attendues dans l'export comptable (lecture par position, pas par
// en-tête : l'intitulé exact des colonnes varie d'un export à l'autre).
const COL_COMPTE = 1; // B — n° de compte, repris à titre indicatif
const COL_DOSSIER = 2; // C — intitulé du dossier client
const COL_CREANCE = 5; // F — créance à recouvrer

const NIVEAUX = ["R1", "R2", "R3"];

const NIVEAU_META = {
  R1: {
    label: "R1",
    court: "1ʳᵉ relance",
    cls: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    desc: "Premier rappel",
  },
  R2: {
    label: "R2",
    court: "2ᵉ relance",
    cls: "bg-amber-100 text-amber-700 border-amber-200",
    dot: "bg-amber-400",
    desc: "Relance ferme",
  },
  R3: {
    label: "R3",
    court: "3ᵉ relance",
    cls: "bg-red-100 text-red-700 border-red-200",
    dot: "bg-red-500",
    desc: "Mise en demeure",
  },
};

const FILTRES = [
  { cle: "actifs", label: "Actifs" },
  { cle: "r1", label: "1ʳᵉ" },
  { cle: "r2", label: "2ᵉ" },
  { cle: "r3", label: "3ᵉ" },
  { cle: "archives", label: "Régularisés" },
  { cle: "tous", label: "Tous" },
];

// Modèles officiels des courriers de relance R1/R2/R3 (LRAR), fournis par
// Samuel/Anthony — voir les PDF "alfa RELANCE N° 1/2/3". Éditables depuis
// "Modèles de mail" (voir TemplatesModal) ; les valeurs saisies sont
// enregistrées dans le navigateur et remplacent ces défauts.
const MODELES_MAIL_DEFAUT = {
  R1: {
    objet: "Relance n° 1 pour impayés",
    corps: `{{cabinet}}

{{nom}}

Au Tampon, le {{date}}


Objet : Relance n° 1 pour impayés

LRAR

Monsieur, Madame,

Nous constatons que votre compte présente toujours un solde débiteur de {{montant}}, correspondant à nos honoraires, qui demeure impayés à ce jour.

Nous vous remercions de nous adresser votre règlement dans les meilleurs délais. Vous trouverez annexé notre RIB.

Nous vous prions d'agréer, Monsieur, Madame, nos salutations distinguées.


{{signataire}}
{{cabinet}}`,
  },
  R2: {
    objet: "Relance n° 2 pour impayés",
    corps: `{{cabinet}}

{{nom}}

Au Tampon, le {{date}}


Objet : Relance n° 2 pour impayés

LRAR

Monsieur, Madame,

Nous constatons que votre compte présente toujours un solde débiteur de {{montant}}, correspondant à nos honoraires, qui malgré une première relance de notre part, demeure impayés à ce jour.

Par conséquent, nous vous remercions de nous adresser votre règlement dans les meilleurs délais. Vous trouverez annexé notre RIB.

A défaut de paiement, nous nous verrons dans l'obligation de poursuivre ce recouvrement par voie judiciaire, après une mise en demeure.

Nous vous prions d'agréer, Monsieur, Madame, nos salutations distinguées.


{{signataire}}
{{cabinet}}`,
  },
  R3: {
    objet: "Relance n° 3 pour impayés avant contentieux",
    corps: `{{cabinet}}

{{nom}}

Au Tampon, le {{date}}


Objet : Relance n° 3 pour impayés avant contentieux

LRAR

Monsieur, Madame,

Nous constatons que votre compte présente toujours un solde débiteur de {{montant}}, correspondant à nos honoraires, qui malgré plusieurs relances de notre part, demeure impayés à ce jour.

Par conséquent, nous vous remercions de nous adresser votre règlement sous huitaine. Vous trouverez annexé notre RIB.

A défaut de paiement, nous nous verrons dans l'obligation de poursuivre une action judiciaire à votre encontre.

Nous vous prions d'agréer, Monsieur, Madame, nos salutations distinguées.


{{signataire}}
{{cabinet}}`,
  },
};

function interpoler(texte, valeurs) {
  return (texte || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (valeurs[k] != null ? valeurs[k] : ""));
}

// Texte du mail qui accompagne le courrier PDF (le courrier lui-même est dans
// la pièce jointe, il n'est pas recopié dans le mail).
const ACCOMPAGNEMENT_NIVEAU = {
  R1: "notre courrier de relance n° 1",
  R2: "notre courrier de relance n° 2",
  R3: "notre dernier courrier de relance avant toute action contentieuse",
};

function accompagnementDefaut(niveau, valeurs) {
  return `Madame, Monsieur,

Veuillez trouver ci-joint ${ACCOMPAGNEMENT_NIVEAU[niveau] || "notre courrier de relance"}, relatif au solde de ${valeurs.montant} restant dû au titre de nos honoraires.

Nous restons à votre disposition pour tout renseignement complémentaire.

Cordialement,
${valeurs.signataire}
${valeurs.cabinet}`;
}

function getModele(niveau, modeles) {
  return modeles[niveau] || MODELES_MAIL_DEFAUT[niveau];
}

function niveauSuivant(niveau) {
  const i = NIVEAUX.indexOf(niveau);
  return i < 0 || i >= NIVEAUX.length - 1 ? "R3" : NIVEAUX[i + 1];
}

// Clé de rapprochement entre deux imports : l'intitulé normalisé (colonne C),
// seule donnée de l'export qui nomme le dossier. Le n° de compte serait plus
// fiable mais n'a pas de correspondance dans MyALFA.
function cleDossier(nom) {
  return String(nom || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

function nombreDepuis(valeur) {
  if (typeof valeur === "number") return isFinite(valeur) ? valeur : null;
  if (typeof valeur !== "string") return null;
  const t = valeur.replace(/\s| |€/g, "").replace(",", ".");
  if (!t || !/^-?\d*\.?\d+$/.test(t)) return null;
  const n = parseFloat(t);
  return isFinite(n) ? n : null;
}

const formatEuros = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateHeure = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// "24 septembre 2026" — pour l'en-tête des courriers de relance ("Au
// Tampon, le ...").
const formatDateLongue = (d) =>
  d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

function niveauBadge(niveau) {
  const meta = NIVEAU_META[niveau] || NIVEAU_META.R1;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium whitespace-nowrap ${meta.cls}`}
    >
      {meta.court}
    </span>
  );
}

// Pastille de niveau cliquable — affichage identique à niveauBadge() au
// repos, ouvre un petit menu pour choisir un autre niveau (correction
// manuelle, sans attendre un réimport).
function NiveauPicker({ niveau, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}>
        {niveauBadge(niveau)}
      </button>
      {open && (
        <div className="absolute z-20 top-full mt-1 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
          {NIVEAUX.map((n) => (
            <button
              key={n}
              onClick={() => {
                onChange(n);
                setOpen(false);
              }}
              className={`flex items-center gap-2 w-full px-3 py-1.5 text-[11px] whitespace-nowrap transition-colors ${
                n === niveau ? "bg-slate-50 font-semibold text-gray-800" : "text-gray-600 hover:bg-slate-50"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${NIVEAU_META[n].dot}`} />
              {NIVEAU_META[n].court}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Une relance est "due" si le dossier est actif et n'a pas encore reçu de
// relance à son niveau courant (jamais relancé, ou relancé à un niveau
// antérieur avant la dernière escalade). Un dossier dont le client est bloqué
// (litige) n'est jamais relancé : voir dossierBloque dans le composant.
function relanceDue(d) {
  return d.statut === "en_cours" && d.actif && d.derniereRelanceNiveau !== d.niveau;
}

/* ── Chrome de modale partagé (import / relance / modèles / journal) ── */
function ModalShell({ onClose, width = 560, children }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col"
        style={{ maxWidth: width }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHead({ icon: Icon, title, subtitle, onClose }) {
  return (
    <div className="px-6 py-4 bg-gradient-to-r from-[#7A1533] to-[#5f1028] flex items-start justify-between gap-3 shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        {Icon && (
          <span className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-white" />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="text-white font-semibold text-sm m-0 truncate">{title}</h3>
          {subtitle && <p className="text-white/70 text-[11px] m-0 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <button
        onClick={onClose}
        aria-label="Fermer"
        className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ── Modale : modèles de mail par niveau (éditables, persistés) ── */
function TemplatesModal({ modeles, onSave, onClose }) {
  const [lvl, setLvl] = useState("R1");
  const [draft, setDraft] = useState(() => {
    const init = {};
    NIVEAUX.forEach((n) => {
      const base = getModele(n, modeles);
      init[n] = { objet: base.objet, corps: base.corps };
    });
    return init;
  });
  const cur = draft[lvl];
  const setChamp = (champ, v) => setDraft((d) => ({ ...d, [lvl]: { ...d[lvl], [champ]: v } }));
  const reinitialiser = () => {
    const init = {};
    NIVEAUX.forEach((n) => {
      init[n] = { objet: MODELES_MAIL_DEFAUT[n].objet, corps: MODELES_MAIL_DEFAUT[n].corps };
    });
    setDraft(init);
  };

  return (
    <ModalShell onClose={onClose} width={680}>
      <ModalHead
        icon={FileText}
        title="Modèles de mail de relance"
        subtitle="Un modèle par niveau — utilisé à l'ouverture d'une relance"
        onClose={onClose}
      />
      <div className="px-6 py-5 space-y-4 overflow-y-auto">
        <div className="inline-flex bg-white border border-gray-200 rounded-lg p-0.5 gap-0.5">
          {NIVEAUX.map((n) => (
            <button
              key={n}
              onClick={() => setLvl(n)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                lvl === n ? "bg-[#6e1a36] text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {n} — {NIVEAU_META[n].desc}
            </button>
          ))}
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Objet</label>
          <input
            type="text"
            value={cur.objet}
            onChange={(e) => setChamp("objet", e.target.value)}
            className="w-full h-9 rounded-lg bg-white border border-gray-200 text-xs px-3 focus:outline-none focus:border-[#840040]"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Corps du mail</label>
          <textarea
            value={cur.corps}
            onChange={(e) => setChamp("corps", e.target.value)}
            rows={12}
            className="w-full rounded-lg bg-white border border-gray-200 text-xs px-3 py-2 leading-relaxed focus:outline-none focus:border-[#840040]"
          />
        </div>
        <p className="text-[10px] text-gray-400 m-0">
          Champs disponibles :{" "}
          <code className="bg-slate-100 px-1 rounded">{"{{cabinet}}"}</code>{" "}
          <code className="bg-slate-100 px-1 rounded">{"{{nom}}"}</code>{" "}
          <code className="bg-slate-100 px-1 rounded">{"{{date}}"}</code>{" "}
          <code className="bg-slate-100 px-1 rounded">{"{{montant}}"}</code>{" "}
          <code className="bg-slate-100 px-1 rounded">{"{{signataire}}"}</code>
        </p>
      </div>
      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center gap-2 shrink-0">
        <button
          onClick={reinitialiser}
          className="text-xs text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg border border-gray-200 bg-white transition-colors"
        >
          Réinitialiser
        </button>
        <span className="flex-1" />
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
        >
          Annuler
        </button>
        <button
          onClick={() => onSave(draft)}
          className="px-4 py-2 rounded-lg bg-[#840040] hover:bg-[#6c0034] text-white text-xs font-semibold transition-colors"
        >
          Enregistrer
        </button>
      </div>
    </ModalShell>
  );
}

/* ── Modale : confirmation de clôture / régularisation (overlay vert) ── */
function ConfirmClotureModal({ dossier, onConfirm, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5 flex flex-col items-center text-center">
          <span className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </span>
          <h3 className="text-sm font-semibold text-gray-900 m-0">Clôturer ce dossier ?</h3>
          <p className="text-xs text-gray-500 mt-2 mb-0">
            Voulez-vous marquer <strong className="text-gray-700">« {dossier.nom} »</strong> comme
            régularisé ? La créance de {formatEuros(dossier.montant)} sera archivée et sortie du suivi
            actif.
          </p>
        </div>
        <div className="px-6 py-4 bg-emerald-50 border-t border-emerald-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
            Clôturer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Modale : rattacher un dossier à un client MyALFA (recherche en mémoire) ── */
function AffectationModal({ dossier, clients, indexClients, onChoisir, onClose }) {
  const [q, setQ] = useState("");
  const proposition = useMemo(() => proposerClient(dossier.nom, indexClients), [dossier.nom, indexClients]);

  const resultats = useMemo(() => {
    const termes = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!termes.length) return [];
    return clients
      .filter((c) => termes.every((t) => (c.nom || "").toLowerCase().includes(t)))
      .slice(0, 25);
  }, [q, clients]);

  return (
    <ModalShell onClose={onClose} width={520}>
      <ModalHead
        icon={Link2}
        title="Rattacher à un client MyALFA"
        subtitle={dossier.nom}
        onClose={onClose}
      />
      <div className="px-6 py-5 space-y-3 overflow-y-auto">
        {proposition && (
          <button
            onClick={() => onChoisir(proposition.client.id)}
            className="w-full text-left px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-xs transition-colors"
          >
            <span className="block text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">
              Proposition ({proposition.force === "exacte" ? "correspondance exacte" : "à vérifier"})
            </span>
            <span className="text-gray-800 font-medium">{proposition.client.nom}</span>
          </button>
        )}
        <input
          type="text"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un client..."
          className="w-full h-9 rounded-lg bg-white border border-gray-200 text-xs px-3 focus:outline-none focus:border-[#840040]"
        />
        <ul className="m-0 p-0 list-none max-h-64 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {resultats.length === 0 ? (
            <li className="px-3 py-4 text-center text-[11px] text-gray-400">
              {q.trim() ? "Aucun client trouvé" : "Tapez le nom du client"}
            </li>
          ) : (
            resultats.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => onChoisir(c.id)}
                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-slate-50"
                >
                  {c.nom}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center gap-2 shrink-0">
        {dossier.clientId && (
          <button
            onClick={() => onChoisir(null)}
            className="text-xs text-gray-500 hover:text-red-500 px-3 py-2 rounded-lg border border-gray-200 bg-white transition-colors"
          >
            Détacher
          </button>
        )}
        <span className="flex-1" />
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
        >
          Fermer
        </button>
      </div>
    </ModalShell>
  );
}

const EMAIL_VALIDE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ── Modale : envoi groupé des relances dues (aperçu, puis envoi confirmé) ── */
function RelancesGroupeesModal({ dossiers, clientsParId, envoyer, onClose }) {
  // Un dossier est envoyable s'il est rattaché à un client dont la fiche a une
  // adresse valide ; sinon il est listé avec la raison et exclu.
  const lignes = useMemo(
    () =>
      dossiers.map((d) => {
        const client = clientsParId.get(d.clientId);
        const email = ((client && client.email) || "").trim();
        let raison = null;
        if (!client) raison = "Aucun client rattaché";
        else if (!EMAIL_VALIDE.test(email)) raison = "Pas d'adresse email sur la fiche client";
        return { d, client, email, raison };
      }),
    [dossiers, clientsParId],
  );

  const [choisis, setChoisis] = useState(
    () => new Set(lignes.filter((l) => !l.raison).map((l) => l.d.cle)),
  );
  const [etat, setEtat] = useState("apercu"); // apercu | envoi | fini
  const [fait, setFait] = useState(0);
  const [echecs, setEchecs] = useState([]);
  const [envoyes, setEnvoyes] = useState(0);
  const arret = useRef(false);

  const envoyables = lignes.filter((l) => !l.raison);
  const aEnvoyer = envoyables.filter((l) => choisis.has(l.d.cle));
  const basculer = (cle) =>
    setChoisis((prev) => {
      const n = new Set(prev);
      if (n.has(cle)) n.delete(cle);
      else n.add(cle);
      return n;
    });

  const lancer = async () => {
    if (!aEnvoyer.length) return;
    const ok = await dialog.confirm(
      `Envoyer ${aEnvoyer.length} relance${aEnvoyer.length > 1 ? "s" : ""} par mail ? Chaque client recevra le courrier PDF correspondant à son niveau. Cette action ne peut pas être annulée.`,
      { title: "Envoyer les relances", confirmLabel: `Envoyer ${aEnvoyer.length} mail${aEnvoyer.length > 1 ? "s" : ""}`, variant: "warning" },
    );
    if (!ok) return;
    arret.current = false;
    setEtat("envoi");
    setFait(0);
    setEnvoyes(0);
    setEchecs([]);
    for (const l of aEnvoyer) {
      if (arret.current) break;
      try {
        await envoyer(l.d, l.email);
        setEnvoyes((n) => n + 1);
      } catch (e) {
        setEchecs((prev) => [...prev, { nom: l.d.nom, message: e.message || "Échec" }]);
      }
      setFait((n) => n + 1);
    }
    setEtat("fini");
  };

  const fermer = () => {
    if (etat === "envoi") return;
    onClose();
  };

  return (
    <ModalShell onClose={fermer} width={760}>
      <ModalHead
        icon={Send}
        title="Lancer les relances"
        subtitle={`${lignes.length} relance${lignes.length > 1 ? "s" : ""} due${lignes.length > 1 ? "s" : ""} · ${envoyables.length} envoyable${envoyables.length > 1 ? "s" : ""}`}
        onClose={fermer}
      />
      <div className="px-6 py-4 overflow-y-auto">
        {etat === "apercu" && (
          <>
            <div className="flex items-start gap-2 px-3 py-2 mb-3 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Les mails partent de la messagerie du cabinet vers l'adresse de la fiche client, avec le
                courrier PDF du niveau en pièce jointe. Décochez ce que vous ne voulez pas envoyer.
              </span>
            </div>
            <div className="flex items-center gap-3 mb-2 text-[11px]">
              <button
                onClick={() => setChoisis(new Set(envoyables.map((l) => l.d.cle)))}
                className="text-[#840040] hover:underline"
              >
                Tout cocher
              </button>
              <button onClick={() => setChoisis(new Set())} className="text-gray-400 hover:underline">
                Tout décocher
              </button>
              <span className="ml-auto text-gray-500">{aEnvoyer.length} sélectionnée{aEnvoyer.length > 1 ? "s" : ""}</span>
            </div>
            <ul className="m-0 p-0 list-none border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[50vh] overflow-y-auto">
              {lignes.map((l) => (
                <li key={l.d.cle} className={`flex items-center gap-3 px-3 py-2 text-xs ${l.raison ? "bg-slate-50/60" : ""}`}>
                  <input
                    type="checkbox"
                    disabled={!!l.raison}
                    checked={!l.raison && choisis.has(l.d.cle)}
                    onChange={() => basculer(l.d.cle)}
                    className="accent-[#840040]"
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate font-medium text-gray-800">{l.d.nom}</span>
                    <span className={`block truncate text-[10px] ${l.raison ? "text-amber-600" : "text-gray-400"}`}>
                      {l.raison ? l.raison : `${l.client.nom} · ${l.email}`}
                    </span>
                  </span>
                  {niveauBadge(l.d.niveau)}
                  <span className="font-mono text-gray-700 w-24 text-right">{formatEuros(l.d.montant)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {etat !== "apercu" && (
          <div className="py-4">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="font-semibold text-gray-800">
                {etat === "envoi" ? "Envoi en cours..." : arret.current ? "Envoi interrompu" : "Envoi terminé"}
              </span>
              <span className="text-gray-500">
                {fait} / {aEnvoyer.length}
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-4">
              <div
                className="h-full bg-[#840040] transition-all"
                style={{ width: `${aEnvoyer.length ? (100 * fait) / aEnvoyer.length : 0}%` }}
              />
            </div>
            <p className="text-xs text-gray-700 m-0">
              <b className="text-emerald-600">{envoyes}</b> relance{envoyes > 1 ? "s" : ""} envoyée{envoyes > 1 ? "s" : ""}
              {echecs.length > 0 && (
                <>
                  {" "}· <b className="text-red-600">{echecs.length}</b> échec{echecs.length > 1 ? "s" : ""}
                </>
              )}
            </p>
            {etat === "envoi" && (
              <p className="text-[10px] text-gray-400 mt-2 mb-0">
                Gardez cette fenêtre ouverte jusqu'à la fin de l'envoi.
              </p>
            )}
            {echecs.length > 0 && (
              <ul className="mt-3 mb-0 p-0 list-none border border-red-100 rounded-lg divide-y divide-red-50 max-h-40 overflow-y-auto">
                {echecs.map((e, i) => (
                  <li key={i} className="px-3 py-1.5 text-[11px] text-red-700">
                    <b>{e.nom}</b> — {e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center gap-2 shrink-0">
        <span className="flex-1" />
        {etat === "envoi" ? (
          <button
            onClick={() => {
              arret.current = true;
            }}
            className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
          >
            Arrêter après l'envoi en cours
          </button>
        ) : (
          <>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
            >
              {etat === "fini" ? "Fermer" : "Annuler"}
            </button>
            {etat === "apercu" && (
              <button
                onClick={lancer}
                disabled={aEnvoyer.length === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#840040] hover:bg-[#6c0034] disabled:opacity-50 text-white text-xs font-semibold transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
                Envoyer {aEnvoyer.length} relance{aEnvoyer.length > 1 ? "s" : ""}
              </button>
            )}
          </>
        )}
      </div>
    </ModalShell>
  );
}

// Dernier état chargé : en revenant sur l'écran (ou après navigation), il
// s'affiche tout de suite pendant que la version à jour est relue.
let cacheRecouvrement = null;

// Ligne de squelette du tableau, mêmes colonnes que les vraies lignes.
function LigneSqueleton() {
  const barre = (w) => <div className="h-3 rounded bg-slate-100 animate-pulse" style={{ width: w }} />;
  return (
    <tr>
      <td className="px-3 py-3.5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-slate-100 animate-pulse flex-shrink-0" />
          <div className="space-y-1.5 flex-1">
            {barre("70%")}
            {barre("30%")}
          </div>
        </div>
      </td>
      <td className="px-3 py-3.5">{barre("65%")}</td>
      <td className="px-3 py-3.5 text-right"><div className="ml-auto">{barre("55px")}</div></td>
      <td className="px-3 py-3.5 text-center"><div className="mx-auto h-5 w-16 rounded-full bg-slate-100 animate-pulse" /></td>
      <td className="px-3 py-3.5">{barre("60px")}</td>
      <td className="px-3 py-3.5">{barre("90px")}</td>
      <td className="px-3 py-3.5"><div className="mx-auto h-4 w-7 rounded-full bg-slate-100 animate-pulse" /></td>
      <td className="px-3 py-3.5 text-right"><div className="ml-auto h-6 w-16 rounded-full bg-slate-100 animate-pulse" /></td>
    </tr>
  );
}

const ValeurSqueleton = ({ w = "60px" }) => (
  <div className="h-6 mt-1 rounded bg-slate-100 animate-pulse" style={{ width: w }} />
);

function Recouvrement() {
  const { utilisateur } = useEtatGlobal();
  const { data: clients = [] } = useClients();
  const indexClients = useMemo(() => indexerClients(clients), [clients]);

  const [dossiers, setDossiers] = useState(() => (cacheRecouvrement ? cacheRecouvrement.dossiers : {}));
  const [journal, setJournal] = useState([]);
  const [journalCharge, setJournalCharge] = useState(false);
  const [imports, setImports] = useState(() => (cacheRecouvrement ? cacheRecouvrement.imports : []));
  const [modeles, setModeles] = useState(() => (cacheRecouvrement ? cacheRecouvrement.modeles : {}));
  const [chargement, setChargement] = useState(!cacheRecouvrement);
  const clientsParId = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  // Dossier dont le client MyALFA est bloqué (litige de recouvrement) : ses
  // relances sont suspendues — ni "dues", ni envoi groupé, ni niveau escaladé
  // à l'import ; le serveur refuse aussi l'envoi (recouvrementController.js).
  const dossierBloque = useCallback(
    (d) => !!d && !!d.clientId && clientsParId.get(d.clientId)?.status === "Bloqué",
    [clientsParId],
  );
  const [erreurApi, setErreurApi] = useState(null);
  const [affecter, setAffecter] = useState(null); // dossier à rattacher à un client

  const [search, setSearch] = useState("");
  const [filtre, setFiltre] = useState("actifs");
  const [page, setPage] = useState(1);

  const [showImport, setShowImport] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [showTpl, setShowTpl] = useState(false);
  const [showGroupe, setShowGroupe] = useState(false);
  const [confirmCloture, setConfirmCloture] = useState(null); // dossier { cle, nom, ... } en attente de confirmation

  const [mail, setMail] = useState(null); // { cle, nom, montant, niveau, to, objet, corps }
  const [copie, setCopie] = useState(false);

  const [fileName, setFileName] = useState("");
  const [rapport, setRapport] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [survol, setSurvol] = useState(false);
  const inputRef = useRef(null);
  const [enImport, setEnImport] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  const auteur =
    `${utilisateur?.name || ""} ${utilisateur?.lastName || ""}`.trim() ||
    utilisateur?.email ||
    "Utilisateur";

  /* ------------------------ persistance ------------------------ */

  const recharger = async () => {
    try {
      const data = await chargerRecouvrement();
      cacheRecouvrement = data;
      setDossiers(data.dossiers);
      setImports(data.imports);
      setModeles(data.modeles);
      setErreurApi(null);
    } catch (e) {
      console.error("Recouvrement — chargement :", e);
      setErreurApi(e.message || "Chargement impossible.");
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    recharger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le journal (jusqu'à 500 lignes) n'est lu qu'à l'ouverture de sa fenêtre.
  useEffect(() => {
    if (!showJournal) return undefined;
    let annule = false;
    setJournalCharge(false);
    chargerJournal()
      .then((j) => {
        if (!annule) {
          setJournal(j);
          setJournalCharge(true);
        }
      })
      .catch((e) => {
        console.error("Recouvrement — journal :", e);
        if (!annule) setJournalCharge(true);
      });
    return () => {
      annule = true;
    };
  }, [showJournal]);

  // Une action serveur qui échoue ne doit jamais laisser l'écran afficher un
  // état qui n'existe pas en base : on le signale et on recharge.
  const echec = async (e) => {
    console.error("Recouvrement :", e);
    await dialog.alert(e.message || "L'opération a échoué.");
    recharger();
  };

  const fusionner = (d) =>
    setDossiers((prev) => {
      const suivant = { ...prev, [d.cle]: d };
      if (cacheRecouvrement) cacheRecouvrement = { ...cacheRecouvrement, dossiers: suivant };
      return suivant;
    });

  // Patch local d'un dossier (mise à jour optimiste, retour arrière), appliqué
  // sur l'état courant plutôt que sur un instantané : une autre action faite
  // entre-temps sur le même dossier n'est jamais écrasée.
  const modifierLocal = (cle, patch) =>
    setDossiers((prev) => {
      if (!prev[cle]) return prev;
      const suivant = { ...prev, [cle]: { ...prev[cle], ...patch } };
      if (cacheRecouvrement) cacheRecouvrement = { ...cacheRecouvrement, dossiers: suivant };
      return suivant;
    });

  const sauverModeles = async (next) => {
    try {
      await enregistrerModeles(next);
      setModeles(next);
      setShowTpl(false);
    } catch (e) {
      echec(e);
    }
  };

  /* ------------------------ import ------------------------ */

  const traiterFichier = async (file) => {
    if (!file) return;
    setErreur(null);
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const lignes = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null });
      analyser(file.name, lignes);
    } catch (e) {
      console.error("Recouvrement — lecture du fichier :", e);
      setErreur(
        "Impossible de lire ce fichier — vérifiez qu'il s'agit bien d'un export Excel (.xls/.xlsx).",
      );
      setRapport(null);
    }
  };

  const analyser = (nomFichier, lignes) => {
    const nouveaux = [];
    const majs = [];
    const clotures = [];
    let zeros = 0;
    let ignorees = 0;
    const vus = new Set();

    (lignes || []).forEach((ligne) => {
      if (!ligne) return;
      const nom = ligne[COL_DOSSIER] == null ? "" : String(ligne[COL_DOSSIER]).trim();
      const montant = nombreDepuis(ligne[COL_CREANCE]);
      // Lignes d'en-tête, de total ou vides : pas de nom ou pas de montant.
      if (!nom || montant === null) {
        ignorees += 1;
        return;
      }
      if (montant === 0) {
        zeros += 1;
        return;
      }

      const cle = cleDossier(nom);
      if (!cle || vus.has(cle)) return;
      vus.add(cle);

      const compte = ligne[COL_COMPTE] == null ? "" : String(ligne[COL_COMPTE]).trim();
      const existant = dossiers[cle];

      if (!existant) {
        const prop = proposerClient(nom, indexClients);
        nouveaux.push({
          cle,
          nom,
          compte,
          montant,
          proposition: prop ? prop.client : null,
          force: prop ? prop.force : null,
          // Les correspondances exactes sont cochées d'office, les probables
          // restent à valider une par une.
          affecter: !!prop && prop.force === "exacte",
        });
      } else if (existant.statut === "archive") {
        clotures.push({ cle, nom, montant });
      } else {
        const bloque = dossierBloque(existant);
        majs.push({
          cle,
          nom,
          compte,
          montant,
          de: existant.niveau,
          // Dossier bloqué (litige) : le montant est mis à jour mais le niveau
          // de relance ne bouge pas.
          vers: bloque ? existant.niveau : niveauSuivant(existant.niveau),
          bloque,
        });
      }
    });

    setRapport({ fichier: nomFichier, nouveaux, majs, clotures, zeros, ignorees });
  };

  const basculerAffectation = (cle) =>
    setRapport((r) => ({
      ...r,
      nouveaux: r.nouveaux.map((d) => (d.cle === cle ? { ...d, affecter: !d.affecter } : d)),
    }));

  const cocherPropositions = (valeur) =>
    setRapport((r) => ({
      ...r,
      nouveaux: r.nouveaux.map((d) => (d.proposition ? { ...d, affecter: valeur } : d)),
    }));

  const confirmerImport = async () => {
    if (!rapport || enImport) return;
    setEnImport(true);
    try {
      await importerRecouvrement({
        fichier: rapport.fichier,
        par: auteur,
        nouveaux: rapport.nouveaux.map((d) => ({
          cle: d.cle,
          nom: d.nom,
          compte: d.compte,
          montant: d.montant,
          clientId: d.affecter && d.proposition ? d.proposition.id : null,
        })),
        majs: rapport.majs.map((d) => ({
          cle: d.cle,
          nom: d.nom,
          compte: d.compte,
          montant: d.montant,
          vers: d.vers,
        })),
      });
      await recharger();
      setRapport(null);
      setFileName("");
      setPage(1);
      if (inputRef.current) inputRef.current.value = "";
      setShowImport(false);
    } catch (e) {
      await echec(e);
    } finally {
      setEnImport(false);
    }
  };

  const annulerImport = () => {
    setRapport(null);
    setFileName("");
    setErreur(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const fermerImportModal = () => {
    annulerImport();
    setShowImport(false);
  };

  /* ------------------------ actions ligne ------------------------ */

  const basculerActif = async (cle) => {
    const d = dossiers[cle];
    if (!d || d.statut === "archive") return;
    fusionner({ ...d, actif: !d.actif });
    try {
      fusionner(await modifierDossier(d.id, { actif: !d.actif, par: auteur }));
    } catch (e) {
      echec(e);
    }
  };

  const changerNiveau = async (cle, niveau) => {
    const d = dossiers[cle];
    if (!d || d.statut === "archive" || d.niveau === niveau) return;
    fusionner({ ...d, niveau });
    try {
      fusionner(await modifierDossier(d.id, { niveau, par: auteur }));
    } catch (e) {
      echec(e);
    }
  };

  // Rattachement / détachement instantané : le tableau est mis à jour et la
  // fenêtre se ferme tout de suite, l'enregistrement se fait en arrière-plan
  // (toast de suivi). En cas d'échec, seul ce rattachement est annulé.
  const rattacherClient = (d, clientId) => {
    const precedent = d.clientId ?? null;
    const client = clientId != null ? clientsParId.get(clientId) : null;
    const rattache = clientId != null;

    modifierLocal(d.cle, { clientId: clientId ?? null });
    setAffecter(null);

    const toast = showStatusToast({
      status: "loading",
      titre: rattache ? "Rattachement du dossier…" : "Détachement du dossier…",
      message: "Enregistrement en cours…",
    });
    modifierDossier(d.id, { clientId: clientId ?? null, par: auteur })
      .then((maj) => {
        fusionner(maj);
        toast.update({
          status: "success",
          titre: rattache ? "Dossier rattaché" : "Dossier détaché",
          message: rattache
            ? `${d.nom} est rattaché à ${client ? client.nom : "ce client"}.`
            : `${d.nom} n'est plus rattaché à un client MyALFA.`,
        });
      })
      .catch((e) => {
        console.error("Recouvrement — rattachement :", e);
        modifierLocal(d.cle, { clientId: precedent });
        toast.update({
          status: "error",
          titre: "Erreur",
          message: rattache
            ? "Le rattachement a échoué, il a été annulé."
            : "Le détachement a échoué, il a été annulé.",
        });
      });
  };

  // Relance : prépare le mail d'accompagnement du courrier, au niveau du
  // dossier. L'adresse du client n'est pas dans l'export comptable : elle est
  // reprise de la fiche client quand le dossier y est rattaché, sinon saisie
  // à la main.
  // Contenu d'une relance (objet, courrier, message d'accompagnement) à partir
  // du modèle du niveau — partagé par la fenêtre de relance et l'envoi groupé.
  const preparerRelance = (d) => {
    const tpl = getModele(d.niveau, modeles);
    const valeurs = {
      nom: d.nom,
      montant: formatEuros(d.montant),
      signataire: auteur,
      cabinet: "ALFA Comptabilité",
      date: formatDateLongue(new Date()),
    };
    return {
      objet: interpoler(tpl.objet, valeurs),
      corps: interpoler(tpl.corps, valeurs),
      accompagnement: accompagnementDefaut(d.niveau, valeurs),
    };
  };

  const ouvrirMail = (d) => {
    setCopie(false);
    setMail({
      cle: d.cle,
      nom: d.nom,
      montant: d.montant,
      niveau: d.niveau,
      to: (clientsParId.get(d.clientId) || {}).email || "",
      ...preparerRelance(d),
    });
  };

  // Envoi d'une relance depuis MyALFA (mail du cabinet + courrier PDF joint),
  // sans fenêtre : utilisé par l'envoi groupé.
  const envoyerRelanceDossier = async (d, to) => {
    const r = preparerRelance(d);
    fusionner(
      await envoyerRelance(d.id, {
        to,
        objet: r.objet,
        message: r.accompagnement,
        nomFichier: nomFichierCourrier(d.niveau, d.nom),
        niveau: d.niveau,
        par: auteur,
        pdfBase64: courrierPdfBase64({ corps: r.corps }),
      }),
    );
  };

  // Messagerie web plutôt que mailto: — sur les postes où Outlook est la
  // messagerie par défaut, mailto: ouvre le client lourd. L'URL de composition
  // a une longueur limitée : un corps trop long est tronqué (le message complet
  // reste copiable avec « Copier le message »).
  const ouvrirDansMessagerie = (fournisseur) => {
    if (!mail) return;
    const LIMITE_CORPS = 1800;
    const tronque = mail.corps.length > LIMITE_CORPS;
    const corps = tronque ? mail.corps.slice(0, LIMITE_CORPS) : mail.corps;
    const to = encodeURIComponent(mail.to || "");
    const su = encodeURIComponent(mail.objet);
    const body = encodeURIComponent(corps);
    const url =
      fournisseur === "outlook"
        ? `https://outlook.office.com/mail/deeplink/compose?to=${to}&subject=${su}&body=${body}`
        : `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${su}&body=${body}`;
    window.open(url, "_blank", "noopener");
    if (tronque) {
      dialog.alert(
        "Le message est long : seul le début a été transmis. Utilisez « Copier le message » puis collez-le dans votre messagerie.",
      );
    }
  };

  const copierMessage = async () => {
    if (!mail) return;
    try {
      await navigator.clipboard.writeText(`${mail.objet}\n\n${mail.corps}`);
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    } catch (e) {
      console.error("Recouvrement — copie impossible :", e);
    }
  };

  const marquerEnvoyee = async () => {
    if (!mail) return;
    const d = dossiers[mail.cle];
    if (!d) return;
    try {
      fusionner(await apiMarquerEnvoyee(d.id, { niveau: mail.niveau, par: auteur }));
      setMail(null);
    } catch (e) {
      echec(e);
    }
  };

  // Envoi depuis MyALFA : mail du cabinet avec le courrier PDF en pièce jointe.
  const envoyerParMyalfa = async () => {
    if (!mail || envoiEnCours) return;
    const to = (mail.to || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      await dialog.alert("Renseignez une adresse de destinataire valide avant d'envoyer.");
      return;
    }
    const ok = await dialog.confirm(
      `Envoyer la relance ${mail.niveau} à ${to} avec le courrier PDF en pièce jointe ?`,
      { title: "Envoyer la relance", confirmLabel: "Envoyer", variant: "default" },
    );
    if (!ok) return;
    const d = dossiers[mail.cle];
    if (!d) return;
    setEnvoiEnCours(true);
    try {
      fusionner(
        await envoyerRelance(d.id, {
          to,
          objet: mail.objet,
          message: mail.accompagnement,
          nomFichier: nomFichierCourrier(mail.niveau, mail.nom),
          niveau: mail.niveau,
          par: auteur,
          pdfBase64: courrierPdfBase64({ corps: mail.corps }),
        }),
      );
      setMail(null);
    } catch (e) {
      await dialog.alert(e.message || "Le mail n'a pas pu être envoyé.");
    } finally {
      setEnvoiEnCours(false);
    }
  };

  const genererPdf = () => {
    if (!mail) return;
    genererCourrierPdf({ niveau: mail.niveau, nom: mail.nom, corps: mail.corps });
  };

  // La confirmation se fait désormais via ConfirmClotureModal (overlay vert
  // dédié) — voir confirmCloture plus bas — plutôt que dialog.confirm.
  const cloturer = async (cle) => {
    const d = dossiers[cle];
    if (!d || d.statut === "archive") return;
    try {
      fusionner(await cloturerDossier(d.id, { par: auteur }));
    } catch (e) {
      echec(e);
    }
  };

  /* ------------------------ dérivés ------------------------ */

  const liste = useMemo(() => {
    const q = search.trim().toLowerCase();
    return Object.entries(dossiers)
      .map(([cle, d]) => ({ ...d, cle }))
      .filter((d) => {
        if (
          q &&
          !d.nom.toLowerCase().includes(q) &&
          !String(d.compte || "").toLowerCase().includes(q)
        )
          return false;
        if (filtre === "actifs") return d.statut === "en_cours";
        if (filtre === "archives") return d.statut === "archive";
        if (filtre === "r1") return d.statut === "en_cours" && d.niveau === "R1";
        if (filtre === "r2") return d.statut === "en_cours" && d.niveau === "R2";
        if (filtre === "r3") return d.statut === "en_cours" && d.niveau === "R3";
        return true; // tous
      })
      .sort((a, b) => {
        if (a.statut !== b.statut) return a.statut === "en_cours" ? -1 : 1;
        const ordre = { R1: 1, R2: 2, R3: 3 };
        return (ordre[b.niveau] - ordre[a.niveau]) || b.montant - a.montant;
      });
  }, [dossiers, search, filtre]);

  const stats = useMemo(() => {
    let actifs = 0;
    let total = 0;
    let archives = 0;
    let due = 0;
    const rep = { R1: 0, R2: 0, R3: 0 };
    Object.values(dossiers).forEach((d) => {
      if (d.statut === "archive") {
        archives += 1;
        return;
      }
      rep[d.niveau] = (rep[d.niveau] || 0) + 1;
      if (d.actif) {
        actifs += 1;
        total += d.montant;
        if (relanceDue(d) && !dossierBloque(d)) due += 1;
      }
    });
    return { actifs, total, rep, archives, due };
  }, [dossiers, dossierBloque]);

  const relancesDues = useMemo(() => {
    const ordre = { R1: 1, R2: 2, R3: 3 };
    return Object.values(dossiers)
      .filter((d) => relanceDue(d) && !dossierBloque(d))
      .sort((a, b) => (ordre[b.niveau] - ordre[a.niveau]) || b.montant - a.montant);
  }, [dossiers, dossierBloque]);

  const pages = Math.max(1, Math.ceil(liste.length / PAGE_SIZE));
  const pageCourante = Math.min(page, pages);
  const pageItems = liste.slice((pageCourante - 1) * PAGE_SIZE, pageCourante * PAGE_SIZE);

  /* ------------------------ rendu ------------------------ */

  return (
    <div className="relative">
      {/* En-tête */}
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900 m-0">Recouvrement</h1>
            <div className="bg-[#9B1B5D]/80 text-white/90 text-xs px-1.5 py-1.5 rounded-full font-medium flex items-center justify-center leading-none">
              {chargement ? "…" : stats.actifs}
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Suivi des impayés, escalade des relances (R1 → R2 → R3) et mails types
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowJournal(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
          >
            <History className="w-3.5 h-3.5" />
            Journal
          </button>
          <button
            onClick={() => setShowTpl(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            Modèles de mail
          </button>
          <button
            onClick={() => setShowGroupe(true)}
            disabled={chargement || stats.due === 0}
            title={stats.due === 0 ? "Aucune relance à envoyer" : "Aperçu puis envoi des relances dues"}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
            Lancer les relances{stats.due ? ` (${stats.due})` : ""}
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            Importer un fichier
          </button>
        </div>
      </div>

      {erreurApi && (
        <div className="mb-5 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-700">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            Impossible de charger le recouvrement ({erreurApi}).{" "}
            <button onClick={recharger} className="underline font-semibold">
              Réessayer
            </button>
          </span>
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-gray-400 m-0">Total dû</p>
          {chargement ? <ValeurSqueleton w="110px" /> : <p className="text-xl font-bold text-[#840040] mt-1 mb-0">{formatEuros(stats.total)}</p>}
          <p className="text-[10px] text-gray-400 mt-0.5 mb-0">
            {stats.actifs} dossier{stats.actifs > 1 ? "s" : ""} actif{stats.actifs > 1 ? "s" : ""}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 flex flex-col justify-center gap-1.5">
          {NIVEAUX.map((n) => (
            <div key={n} className="flex items-center gap-2 text-xs">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${NIVEAU_META[n].dot}`} />
              <span className="text-gray-500 flex-1 truncate">{NIVEAU_META[n].court}</span>
              {chargement ? (
                <span className="h-3.5 w-6 rounded bg-slate-100 animate-pulse" />
              ) : (
                <span className="font-bold text-gray-800">{stats.rep[n] || 0}</span>
              )}
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-gray-400 m-0">Relances à envoyer</p>
          {chargement ? (
            <ValeurSqueleton w="30px" />
          ) : (
            <p className={`text-xl font-bold mt-1 mb-0 ${stats.due ? "text-amber-600" : "text-emerald-600"}`}>
              {stats.due}
            </p>
          )}
          <p className="text-[10px] text-gray-400 mt-0.5 mb-0">en attente d'envoi</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-gray-400 m-0">Régularisés</p>
          {chargement ? <ValeurSqueleton w="30px" /> : <p className="text-xl font-bold text-gray-800 mt-1 mb-0">{stats.archives}</p>}
          <p className="text-[10px] text-gray-400 mt-0.5 mb-0">dossiers soldés</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-gray-400 m-0">Imports</p>
          {chargement ? <ValeurSqueleton w="30px" /> : <p className="text-xl font-bold text-gray-800 mt-1 mb-0">{imports.length}</p>}
          <p className="text-[10px] text-gray-400 mt-0.5 mb-0">
            dernier : {imports.length ? formatDate(imports[0].date) : "—"}
          </p>
        </div>
      </div>

      {/* Barre d'outils */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher un dossier..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-8 h-8 w-full rounded-full bg-white border border-gray-200 text-xs focus:outline-none focus:border-gray-300"
          />
        </div>
        <div className="inline-flex bg-white border border-gray-200 rounded-lg p-0.5 gap-0.5">
          {FILTRES.map((f) => (
            <button
              key={f.cle}
              onClick={() => {
                setFiltre(f.cle);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filtre === f.cle ? "bg-[#6e1a36] text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-gray-400 ml-auto">
          {liste.length} dossier{liste.length > 1 ? "s" : ""}
        </span>
      </div>

      {/* Tableau */}
      <div className="w-full overflow-x-auto">
        <table
          className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
          style={{ fontSize: "0.68rem" }}
        >
          <thead>
            <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Dossier
              </th>
              <th className="px-3 py-2.5"></th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-right uppercase tracking-wide">
                Montant dû
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-center uppercase tracking-wide">
                Niveau
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Dernière relance
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Statut
              </th>
              <th className="px-3 py-2.5"></th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-right uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {chargement && Object.keys(dossiers).length === 0 ? (
              Array.from({ length: 8 }, (_, i) => <LigneSqueleton key={i} />)
            ) : pageItems.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-16 text-center text-gray-400 text-xs">
                  {filtre === "archives"
                    ? "Aucun dossier clôturé"
                    : "Aucun dossier — importez l'export comptable pour commencer"}
                </td>
              </tr>
            ) : (
              pageItems.map((d) => {
                const bloque = dossierBloque(d) && d.statut !== "archive";
                const due = relanceDue(d) && !bloque;
                // Dossier désactivé (coche éteinte) : toute la ligne est grisée
                // et plus rien n'y est cliquable ni sélectionnable, sauf la
                // coche elle-même (sinon impossible de le réactiver). Dossier
                // bloqué (litige) : grisé de la même façon, coche comprise —
                // il se débloque depuis la fiche client. `inert` bloque aussi
                // le focus clavier et les infobulles.
                const gele = (!d.actif || bloque) && d.statut !== "archive";
                const gel = gele ? "pointer-events-none select-none" : "";
                return (
                  <tr
                    key={d.cle}
                    className={`transition-colors hover:bg-slate-50 ${
                      bloque
                        ? "ligne-bloquee"
                        : d.statut === "archive" || !d.actif
                          ? "opacity-60"
                          : ""
                    }`}
                  >
                    <td className={`px-3 py-2.5 ${gel}`} inert={gele}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#f5ede8] flex items-center justify-center text-[#840040] font-semibold flex-shrink-0 text-[9px]">
                          {(d.nom || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 m-0 truncate">
                            {d.nom}
                            {bloque && <BadgeBloque />}
                          </p>
                          {d.compte && (
                            <p className="text-[10px] text-gray-400 font-mono m-0">{d.compte}</p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className={`px-3 py-2.5 ${gel}`} inert={gele}>
                      {(() => {
                        const cl = clientsParId.get(d.clientId);
                        if (d.statut === "archive") {
                          return cl ? <span className="text-gray-600">{cl.nom}</span> : <span className="text-gray-300">—</span>;
                        }
                        return (
                          <button
                            type="button"
                            onClick={() => setAffecter(d)}
                            title={cl ? "Changer de client" : "Rattacher à un client MyALFA"}
                            className={
                              cl
                                ? "inline-flex items-center gap-1 text-gray-700 hover:text-[#840040] text-left"
                                : "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-medium hover:bg-amber-100"
                            }
                          >
                            <Link2 className="w-3 h-3 shrink-0" />
                            {cl ? cl.nom : "À affecter"}
                          </button>
                        );
                      })()}
                    </td>

                    <td
                      className={`px-3 py-2.5 text-right font-mono font-medium whitespace-nowrap ${gel} ${
                        d.montant < 0 ? "text-emerald-600" : "text-gray-800"
                      }`}
                      inert={gele}
                    >
                      {formatEuros(d.montant)}
                    </td>

                    <td className={`px-3 py-2.5 text-center ${gel}`} inert={gele}>
                      {d.statut === "archive" ? (
                        niveauBadge(d.niveau)
                      ) : (
                        <NiveauPicker niveau={d.niveau} onChange={(n) => changerNiveau(d.cle, n)} />
                      )}
                    </td>

                    <td className={`px-3 py-2.5 text-gray-500 whitespace-nowrap ${gel}`} inert={gele}>
                      {d.derniereRelanceLe ? (
                        <>
                          {formatDate(d.derniereRelanceLe)}{" "}
                          <span className="text-gray-300">· {d.derniereRelanceNiveau}</span>
                        </>
                      ) : (
                        <span className="text-gray-300">Jamais</span>
                      )}
                    </td>

                    <td className={`px-3 py-2.5 whitespace-nowrap ${gel}`} inert={gele}>
                      {d.statut === "archive" ? (
                        <span className="text-[11px] text-gray-400">
                          Clôturé le {formatDate(d.clotureLe)}
                        </span>
                      ) : bloque ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400">
                          Relances suspendues
                        </span>
                      ) : !d.actif ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400">
                          Non relancé
                        </span>
                      ) : due ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                          <AlertCircle className="w-3 h-3" />
                          Relance à envoyer
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                          <CheckCircle2 className="w-3 h-3" />
                          Relance {d.derniereRelanceNiveau} envoyée
                        </span>
                      )}
                    </td>

                    <td
                      className={`px-3 py-2.5 text-center ${bloque ? "pointer-events-none select-none" : ""}`}
                      inert={bloque}
                    >
                      {d.statut === "archive" ? (
                        <span className="text-[10px] text-gray-300">—</span>
                      ) : (
                        <button
                          onClick={() => basculerActif(d.cle)}
                          aria-pressed={d.actif}
                          aria-label={`${d.actif ? "Désactiver" : "Activer"} ${d.nom}`}
                          title={d.actif ? "Désactiver (exclure du total)" : "Réactiver"}
                          className={`relative inline-flex w-7 h-4 rounded-full transition-colors align-middle ${
                            d.actif ? "bg-[#840040]" : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${
                              d.actif ? "left-[14px]" : "left-0.5"
                            }`}
                          />
                        </button>
                      )}
                    </td>

                    <td className={`px-3 py-2.5 text-right whitespace-nowrap ${gel}`} inert={gele}>
                      {d.statut === "archive" ? (
                        <span className="text-[10px] text-gray-300">—</span>
                      ) : (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => ouvrirMail(d)}
                            title={`Préparer la relance ${d.niveau} — mail`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#840040] hover:bg-[#6c0034] text-white text-[10px] font-medium transition-colors"
                          >
                            <Send className="w-2.5 h-2.5" />
                            Relancer
                          </button>
                          <button
                            onClick={() => setConfirmCloture(d)}
                            title="Clôturer"
                            className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white text-gray-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={pageCourante === 1}
            className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 text-xs"
          >
            ‹
          </button>
          <span className="text-[11px] text-gray-500 px-2">
            Page {pageCourante} sur {pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={pageCourante === pages}
            className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 text-xs"
          >
            ›
          </button>
        </div>
      )}

      {/* Historique des imports */}
      {imports.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 mt-5">
          <div className="flex items-center gap-2 mb-3">
            <Archive className="w-4 h-4 text-[#9B1B5D]" />
            <h2 className="text-sm font-semibold text-gray-900 m-0">Historique des imports</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {imports.map((im, i) => (
              <div key={i} className="flex items-center gap-3 py-2 text-xs flex-wrap">
                <span className="text-gray-500 font-medium w-24 shrink-0">{formatDate(im.date)}</span>
                <span className="text-gray-400 flex-1 min-w-0 truncate">
                  {im.fichier} · <b className="text-emerald-600 font-semibold">{im.nouveaux}</b> nouveaux
                  · <b className="text-amber-600 font-semibold">{im.majs}</b> relances escaladées
                </span>
                <span className="text-gray-400 shrink-0">{im.par}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modale : importer un fichier */}
      {showImport && (
        <ModalShell onClose={fermerImportModal} width={680}>
          <ModalHead
            icon={Upload}
            title="Importer l'export comptable"
            subtitle="Colonne C lue comme dossier client, colonne F comme créance à recouvrer"
            onClose={fermerImportModal}
          />
          <div className="px-6 py-5 space-y-4 overflow-y-auto">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setSurvol(true);
              }}
              onDragLeave={() => setSurvol(false)}
              onDrop={(e) => {
                e.preventDefault();
                setSurvol(false);
                traiterFichier(e.dataTransfer.files?.[0]);
              }}
              onClick={() => inputRef.current?.click()}
              className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-all ${
                survol
                  ? "border-[#840040] bg-[#840040]/[0.04]"
                  : "border-slate-200 hover:border-[#840040]/40 hover:bg-[#840040]/[0.02]"
              }`}
            >
              <Upload className="w-5 h-5 text-[#840040] mx-auto mb-1.5" />
              <p className="text-xs font-medium text-gray-700 m-0">
                Déposez le fichier .xlsx ici, ou cliquez pour le choisir
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5 mb-0">
                Le premier onglet du classeur est lu
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => traiterFichier(e.target.files?.[0])}
              />
            </div>

            {fileName && !rapport && !erreur && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-700 w-fit">
                <FileSpreadsheet className="w-4 h-4 text-[#5C1A30]" />
                {fileName}
                <button onClick={annulerImport} className="text-gray-400 hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {erreur && (
              <p className="text-[11px] text-red-600 mb-0 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {erreur}
              </p>
            )}

            {rapport && (
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                <p className="text-xs font-semibold text-gray-900 m-0 mb-3">
                  Aperçu de l'import — « {rapport.fichier} »
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <div className="bg-white border border-[#840040]/20 rounded-lg px-3 py-2">
                    <p className="text-base font-semibold text-[#840040] m-0">
                      {rapport.nouveaux.length}
                    </p>
                    <p className="text-[10px] text-gray-500 m-0">nouveaux dossiers, créés en R1</p>
                  </div>
                  <div className="bg-white border border-[#840040]/20 rounded-lg px-3 py-2">
                    <p className="text-base font-semibold text-[#840040] m-0">{rapport.majs.length}</p>
                    <p className="text-[10px] text-gray-500 m-0">déjà suivis, relance incrémentée</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <p className="text-base font-semibold text-gray-700 m-0">
                      {rapport.clotures.length}
                    </p>
                    <p className="text-[10px] text-gray-500 m-0">déjà clôturés, non réactivés</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <p className="text-base font-semibold text-gray-700 m-0">{rapport.zeros}</p>
                    <p className="text-[10px] text-gray-500 m-0">lignes à 0 €, ignorées</p>
                  </div>
                </div>

                {/* Rapprochement des nouveaux dossiers avec les clients MyALFA
                    (même règle que l'affectation depuis le tableau : proposerClient) */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <p className="text-base font-semibold text-emerald-600 m-0">
                      {rapport.nouveaux.filter((d) => d.force === "exacte").length}
                    </p>
                    <p className="text-[10px] text-gray-500 m-0">liés à un client MyALFA</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <p className="text-base font-semibold text-gray-700 m-0">
                      {rapport.nouveaux.filter((d) => !d.proposition).length}
                    </p>
                    <p className="text-[10px] text-gray-500 m-0">non liés, aucun client trouvé</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <p className="text-base font-semibold text-amber-600 m-0">
                      {rapport.nouveaux.filter((d) => d.force === "probable").length}
                    </p>
                    <p className="text-[10px] text-gray-500 m-0">ressemblances, à vérifier</p>
                  </div>
                </div>

                {rapport.nouveaux.some((d) => d.proposition) && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <p className="text-[11px] font-semibold text-gray-700 m-0 flex-1">
                        Rattachement proposé aux clients MyALFA (
                        {rapport.nouveaux.filter((d) => d.affecter).length} sur{" "}
                        {rapport.nouveaux.filter((d) => d.proposition).length} cochés)
                      </p>
                      <button
                        type="button"
                        onClick={() => cocherPropositions(true)}
                        className="text-[10px] text-[#840040] hover:underline"
                      >
                        Tout cocher
                      </button>
                      <button
                        type="button"
                        onClick={() => cocherPropositions(false)}
                        className="text-[10px] text-gray-400 hover:underline"
                      >
                        Tout décocher
                      </button>
                    </div>
                    <ul className="m-0 p-0 list-none max-h-56 overflow-y-auto border border-slate-200 rounded-lg bg-white divide-y divide-slate-100">
                      {rapport.nouveaux
                        .filter((d) => d.proposition)
                        .map((d) => (
                          <li key={d.cle} className="flex items-center gap-2 px-3 py-1.5 text-[11px]">
                            <input
                              type="checkbox"
                              checked={d.affecter}
                              onChange={() => basculerAffectation(d.cle)}
                              className="accent-[#840040]"
                            />
                            <span className="text-gray-700 truncate flex-1 min-w-0">{d.nom}</span>
                            <span className="text-gray-300">→</span>
                            <span className="text-gray-800 font-medium truncate flex-1 min-w-0">
                              {d.proposition.nom}
                            </span>
                            <span
                              className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                                d.force === "exacte"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-amber-50 text-amber-700"
                              }`}
                            >
                              {d.force === "exacte" ? "Exacte" : "À vérifier"}
                            </span>
                          </li>
                        ))}
                    </ul>
                    <p className="text-[10px] text-gray-400 m-0 mt-1">
                      Les autres dossiers restent « À affecter » : choisissez le client depuis le tableau.
                    </p>
                  </div>
                )}

                {rapport.majs.length > 0 && (
                  <>
                    {rapport.majs.some((m) => !m.bloque) && (
                      <p className="text-[11px] text-gray-600 m-0">
                        Passages de niveau :{" "}
                        {NIVEAUX.map((n) => {
                          const nb = rapport.majs.filter((m) => m.de === n && !m.bloque).length;
                          if (!nb) return null;
                          return (
                            <span key={n} className="mr-2">
                              {nb} en {n} → {niveauSuivant(n)}
                              {n === "R3" ? " (inchangé)" : ""}
                            </span>
                          );
                        })}
                      </p>
                    )}
                    {rapport.majs.some((m) => m.bloque) && (
                      <p className="text-[11px] text-red-700 m-0 mt-1">
                        {rapport.majs.filter((m) => m.bloque).length} dossier
                        {rapport.majs.filter((m) => m.bloque).length > 1 ? "s" : ""} bloqué
                        {rapport.majs.filter((m) => m.bloque).length > 1 ? "s" : ""} (litige) : niveau inchangé,
                        montant mis à jour.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center gap-2 shrink-0">
            <span className="flex-1" />
            {rapport ? (
              <>
                <button
                  onClick={annulerImport}
                  className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmerImport}
                  disabled={enImport}
                  className="px-4 py-2 rounded-lg bg-[#840040] hover:bg-[#6c0034] disabled:opacity-60 text-white text-xs font-semibold transition-colors"
                >
                  {enImport ? "Import en cours..." : "Confirmer l'import"}
                </button>
              </>
            ) : (
              <span className="text-[11px] text-gray-400">
                Déposez ou sélectionnez un fichier pour continuer.
              </span>
            )}
          </div>
        </ModalShell>
      )}

      {/* Modale : journal des mouvements */}
      {showJournal && (
        <ModalShell onClose={() => setShowJournal(false)} width={640}>
          <ModalHead
            icon={History}
            title="Journal des mouvements"
            subtitle={journalCharge ? `${journal.length} entrée${journal.length > 1 ? "s" : ""}` : "Chargement du journal"}
            onClose={() => setShowJournal(false)}
          />
          <div className="overflow-y-auto">
            {!journalCharge ? (
              <ul className="m-0 p-0 list-none divide-y divide-slate-100">
                {Array.from({ length: 8 }, (_, i) => (
                  <li key={i} className="flex items-center gap-3 px-6 py-3">
                    <div className="h-3 w-24 rounded bg-slate-100 animate-pulse" />
                    <div className="h-3 flex-1 rounded bg-slate-100 animate-pulse" />
                  </li>
                ))}
              </ul>
            ) : journal.length === 0 ? (
              <p className="py-16 text-center text-gray-400 text-xs m-0">
                Aucun mouvement enregistré pour le moment
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 m-0 p-0 list-none">
                {journal.map((e, i) => (
                  <li key={i} className="flex items-baseline gap-3 px-6 py-2.5 text-xs">
                    <span className="text-[10px] text-gray-400 font-mono whitespace-nowrap">
                      {formatDateHeure(e.date)}
                    </span>
                    <span className="text-gray-700">
                      <strong className="font-semibold">{e.nom}</strong>
                      {e.type === "cloture"
                        ? ` — Recouvrement terminé · Clôture par ${e.par}`
                        : e.type === "envoi"
                          ? ` — Recouvrement · Relance niveau ${e.niveau} envoyée par ${e.par}`
                          : ` — Recouvrement · Relance niveau ${e.niveau}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </ModalShell>
      )}

      {/* Modale : modèles de mail */}
      {showTpl && (
        <TemplatesModal modeles={modeles} onSave={sauverModeles} onClose={() => setShowTpl(false)} />
      )}

      {/* Modale : confirmation de clôture */}
      {confirmCloture && (
        <ConfirmClotureModal
          dossier={confirmCloture}
          onClose={() => setConfirmCloture(null)}
          onConfirm={() => {
            cloturer(confirmCloture.cle);
            setConfirmCloture(null);
          }}
        />
      )}

      {/* Modale : envoi groupé des relances dues */}
      {showGroupe && (
        <RelancesGroupeesModal
          dossiers={relancesDues}
          clientsParId={clientsParId}
          envoyer={envoyerRelanceDossier}
          onClose={() => setShowGroupe(false)}
        />
      )}

      {/* Modale : rattacher un dossier à un client MyALFA */}
      {affecter && (
        <AffectationModal
          dossier={affecter}
          clients={clients}
          indexClients={indexClients}
          onChoisir={(id) => rattacherClient(affecter, id)}
          onClose={() => setAffecter(null)}
        />
      )}

      {/* Modale : relance (mail d'accompagnement) */}
      {mail && (
        <ModalShell onClose={() => setMail(null)} width={680}>
          <ModalHead
            icon={Mail}
            title={`${mail.niveau} — ${mail.nom}`}
            subtitle={`${NIVEAU_META[mail.niveau].desc} · Créance de ${formatEuros(mail.montant)}`}
            onClose={() => setMail(null)}
          />
          <div className="px-6 py-5 overflow-y-auto">
            <div className="flex items-start gap-2 px-3 py-2 mb-4 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Texte {modeles[mail.niveau] ? "personnalisé (voir « Modèles de mail »)" : "issu du modèle officiel"}.
                Relisez-le avant tout envoi à un client.
              </span>
            </div>

            <label className="block text-[11px] font-medium text-gray-600 mb-1">Destinataire</label>
            <input
              type="email"
              value={mail.to}
              onChange={(e) => setMail({ ...mail, to: e.target.value })}
              placeholder="adresse@client.fr"
              className="w-full h-9 rounded-lg bg-white border border-gray-200 text-xs px-3 mb-1 focus:outline-none focus:border-[#840040]"
            />
            <p className="text-[10px] text-gray-400 mt-0 mb-4">
              {dossiers[mail.cle] && dossiers[mail.cle].clientId
                ? "Adresse reprise de la fiche client si elle existe, modifiable."
                : "Dossier non rattaché à un client : adresse à saisir à la main."}
            </p>

            <label className="block text-[11px] font-medium text-gray-600 mb-1">Objet</label>
            <input
              type="text"
              value={mail.objet}
              onChange={(e) => setMail({ ...mail, objet: e.target.value })}
              className="w-full h-9 rounded-lg bg-white border border-gray-200 text-xs px-3 mb-4 focus:outline-none focus:border-[#840040]"
            />

            <label className="block text-[11px] font-medium text-gray-600 mb-1">
              Courrier (contenu du PDF joint)
            </label>
            <textarea
              value={mail.corps}
              onChange={(e) => setMail({ ...mail, corps: e.target.value })}
              rows={11}
              className="w-full rounded-lg bg-white border border-gray-200 text-xs px-3 py-2 leading-relaxed mb-4 focus:outline-none focus:border-[#840040]"
            />

            <label className="block text-[11px] font-medium text-gray-600 mb-1">
              Message d'accompagnement (corps du mail envoyé avec le PDF)
            </label>
            <textarea
              value={mail.accompagnement}
              onChange={(e) => setMail({ ...mail, accompagnement: e.target.value })}
              rows={8}
              className="w-full rounded-lg bg-white border border-gray-200 text-xs px-3 py-2 leading-relaxed focus:outline-none focus:border-[#840040]"
            />
          </div>
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center gap-2 flex-wrap shrink-0">
            <button
              onClick={envoyerParMyalfa}
              disabled={envoiEnCours}
              title="Envoie le mail depuis la messagerie du cabinet, avec le courrier PDF en pièce jointe"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#840040] hover:bg-[#6c0034] disabled:opacity-60 text-white text-xs font-semibold transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              {envoiEnCours ? "Envoi..." : "Envoyer avec le PDF"}
            </button>
            <button
              onClick={() => ouvrirDansMessagerie("gmail")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              Ouvrir dans Gmail
            </button>
            <button
              onClick={() => ouvrirDansMessagerie("outlook")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              Ouvrir dans Outlook
            </button>
            <button
              onClick={copierMessage}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
              {copie ? "Copié" : "Copier le message"}
            </button>
            <button
              onClick={genererPdf}
              title="Télécharger le courrier en PDF, signé par le cabinet (sans image de signature)"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
            >
              <FileText className="w-3.5 h-3.5" />
              Générer le courrier {mail.niveau}
            </button>
            <button
              onClick={marquerEnvoyee}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors ml-auto"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Déjà envoyée, marquer
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

export default Recouvrement;
